/** Client-side mesh loaders: STL, OBJ, 3MF (mesh only). Output Y-up Float32 positions. */

function stlBinaryToYUp(buf: ArrayBuffer): Float32Array {
  const dv = new DataView(buf);
  const tris = dv.getUint32(80, true);
  const out = new Float32Array(tris * 9);
  let o = 84;
  let p = 0;
  for (let i = 0; i < tris; i++) {
    o += 12; // skip normal
    for (let v = 0; v < 3; v++) {
      const x = dv.getFloat32(o, true);
      const y = dv.getFloat32(o + 4, true);
      const z = dv.getFloat32(o + 8, true);
      // Z-up STL → Y-up
      out[p++] = x;
      out[p++] = z;
      out[p++] = -y;
      o += 12;
    }
    o += 2;
  }
  return out;
}

function isAsciiStl(buf: ArrayBuffer): boolean {
  const head = new TextDecoder().decode(buf.slice(0, 80)).toLowerCase();
  return head.includes('solid') && !head.includes('\0');
}

function stlAsciiToYUp(text: string): Float32Array {
  const verts: number[] = [];
  const re = /vertex\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const x = parseFloat(m[1]);
    const y = parseFloat(m[2]);
    const z = parseFloat(m[3]);
    verts.push(x, z, -y);
  }
  return new Float32Array(verts);
}

async function loadStl(file: File): Promise<Float32Array> {
  const buf = await file.arrayBuffer();
  if (isAsciiStl(buf)) {
    return stlAsciiToYUp(new TextDecoder().decode(buf));
  }
  return stlBinaryToYUp(buf);
}

async function loadObj(file: File): Promise<Float32Array> {
  const text = await file.text();
  const positions: number[] = [];
  const verts: number[][] = [];
  for (const line of text.split(/\r?\n/)) {
    const p = line.trim().split(/\s+/);
    if (p[0] === 'v') verts.push([+p[1], +p[2], +p[3]]);
    else if (p[0] === 'f') {
      const idx = p.slice(1).map((t) => parseInt(t.split('/')[0], 10) - 1);
      for (let i = 1; i + 1 < idx.length; i++) {
        for (const vi of [idx[0], idx[i], idx[i + 1]]) {
          const v = verts[vi];
          // OBJ often Y-up already; keep as-is
          positions.push(v[0], v[1], v[2]);
        }
      }
    }
  }
  return new Float32Array(positions);
}

/** Minimal 3MF mesh extract (first object mesh vertices/triangles). */
async function load3mf(file: File): Promise<Float32Array> {
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const modelFile =
    zip.file(/3D\/.*\.model$/i)[0] ||
    zip.file(/.*\.model$/i)[0];
  if (!modelFile) throw new Error('No 3D model found inside 3MF');
  const xml = await modelFile.async('string');
  const verts: number[][] = [];
  const positions: number[] = [];
  const vRe = /<vertex\s+x="([^"]+)"\s+y="([^"]+)"\s+z="([^"]+)"/gi;
  let m: RegExpExecArray | null;
  while ((m = vRe.exec(xml))) verts.push([+m[1], +m[2], +m[3]]);
  const tRe = /<triangle\s+v1="(\d+)"\s+v2="(\d+)"\s+v3="(\d+)"/gi;
  while ((m = tRe.exec(xml))) {
    for (const vi of [+m[1], +m[2], +m[3]]) {
      const v = verts[vi];
      // 3MF is typically Z-up → Y-up
      positions.push(v[0], v[2], -v[1]);
    }
  }
  if (!positions.length) throw new Error('Empty mesh in 3MF');
  return new Float32Array(positions);
}

export interface LoadedMesh {
  name: string;
  positions: Float32Array;
}

export async function loadMeshFile(file: File): Promise<LoadedMesh> {
  const name = file.name.replace(/\.(stl|obj|3mf)$/i, '');
  const ext = file.name.split('.').pop()?.toLowerCase();
  let positions: Float32Array;
  if (ext === 'stl') positions = await loadStl(file);
  else if (ext === 'obj') positions = await loadObj(file);
  else if (ext === '3mf') positions = await load3mf(file);
  else throw new Error(`Unsupported format: ${ext}`);
  if (positions.length < 9) throw new Error('Mesh has no triangles');
  return { name, positions };
}
